import { useEffect, useRef, useState } from "react";
import { useAui, useAuiState } from "@assistant-ui/react";
import AssistantAvatar from "./AssistantAvatar";
import LendyIcon from "../components/lendy/LendyIcon";
import { getSpeechRecognition, toSpokenText } from "./voice";

// Errors after which listening again would fail the same way: the mic is blocked or missing.
const BLOCKED_MIC_ERRORS = new Set(["not-allowed", "service-not-allowed", "audio-capture"]);

function selectLatestAnswer(state) {
  const { messages } = state.thread;
  const last = messages[messages.length - 1];
  if (!last || last.role !== "assistant") {
    return "";
  }

  return last.content
    .filter((part) => part.type === "text")
    .map((part) => part.text)
    .join("\n\n");
}

/**
 * Voice mode (board 14): listen, send what was said as the next question, read the answer aloud,
 * then listen again. `phase` is off, listening, working or speaking; muting keeps the mic closed.
 */
export function useVoiceMode() {
  const aui = useAui();
  const isRunning = useAuiState((state) => state.thread.isRunning);
  const latestAnswer = useAuiState(selectLatestAnswer);
  const [phase, setPhase] = useState("off");
  const [isMuted, setIsMuted] = useState(false);
  const [heard, setHeard] = useState("");
  const [error, setError] = useState("");
  // Speech callbacks fire long after the render that created them, so they read these refs.
  const phaseRef = useRef(phase);
  const isMutedRef = useRef(isMuted);
  const answerRef = useRef(latestAnswer);
  const recognitionRef = useRef(null);
  const wasRunningRef = useRef(isRunning);
  answerRef.current = latestAnswer;

  const moveTo = (nextPhase) => {
    phaseRef.current = nextPhase;
    setPhase(nextPhase);
  };

  const stopListening = () => {
    const recognition = recognitionRef.current;
    recognitionRef.current = null;
    recognition?.abort();
  };

  const listen = () => {
    if (isMutedRef.current) {
      moveTo("muted");

      return;
    }

    const SpeechRecognition = getSpeechRecognition();
    const recognition = new SpeechRecognition();
    let said = "";
    recognition.lang = navigator.language || "en-US";
    recognition.interimResults = true;
    recognition.onresult = (event) => {
      said = Array.from(event.results)
        .map((result) => result[0].transcript)
        .join("");
      setHeard(said);
    };
    recognition.onerror = (event) => {
      if (!BLOCKED_MIC_ERRORS.has(event.error)) {
        return;
      }

      setError("Allow the microphone to use voice mode.");
      moveTo("off");
    };
    recognition.onend = () => {
      if (recognitionRef.current !== recognition || phaseRef.current !== "listening") {
        return;
      }

      recognitionRef.current = null;
      const question = said.trim();
      if (!question) {
        listen();

        return;
      }

      moveTo("working");
      aui.thread().append(question);
    };
    recognitionRef.current = recognition;
    setHeard("");
    moveTo("listening");
    recognition.start();
  };

  const speak = (markdown) => {
    const text = toSpokenText(markdown);
    if (!text) {
      listen();

      return;
    }

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = navigator.language || "en-US";
    utterance.onend = () => {
      if (phaseRef.current === "speaking") {
        listen();
      }
    };
    utterance.onerror = utterance.onend;
    window.speechSynthesis.cancel();
    moveTo("speaking");
    window.speechSynthesis.speak(utterance);
  };

  useEffect(() => {
    const hasStarted = isRunning && !wasRunningRef.current;
    const hasFinished = !isRunning && wasRunningRef.current;
    wasRunningRef.current = isRunning;
    if (phaseRef.current === "off") {
      return;
    }

    if (hasStarted) {
      stopListening();
      window.speechSynthesis.cancel();
      moveTo("working");

      return;
    }

    if (hasFinished) {
      speak(answerRef.current);
    }
  }, [isRunning]); // eslint-disable-line

  useEffect(
    () => () => {
      recognitionRef.current?.abort();
      window.speechSynthesis?.cancel();
    },
    []
  );

  const start = () => {
    setError("");
    isMutedRef.current = false;
    setIsMuted(false);
    if (isRunning) {
      moveTo("working");

      return;
    }

    listen();
  };

  const end = () => {
    stopListening();
    window.speechSynthesis.cancel();
    setHeard("");
    moveTo("off");
  };

  const toggleMute = () => {
    const nextIsMuted = !isMutedRef.current;
    isMutedRef.current = nextIsMuted;
    setIsMuted(nextIsMuted);
    if (nextIsMuted && phaseRef.current === "listening") {
      stopListening();
      moveTo("muted");

      return;
    }

    if (!nextIsMuted && phaseRef.current === "muted") {
      listen();
    }
  };

  return { phase, isMuted, heard, error, start, end, toggleMute };
}

function getDockText({ phase, heard, workLabel, assistantName }) {
  if (phase === "listening") {
    return { title: "Listening…", detail: heard || "Ask about this loan, or give me a task" };
  }
  if (phase === "working") {
    return { title: "Working on it", detail: workLabel || "Thinking…" };
  }
  if (phase === "speaking") {
    return { title: `${assistantName} is answering`, detail: "The answer stays in this chat" };
  }

  return { title: "Mic is off", detail: "Turn the mic on to talk" };
}

/** Takes the composer's place while voice mode is on. */
export function VoiceDock({ voice, workLabel, assistantName, imageUrl }) {
  const { phase, isMuted, heard, toggleMute, end } = voice;
  const { title, detail } = getDockText({ phase, heard, workLabel, assistantName });
  const isSoundActive = phase === "listening" || phase === "speaking";

  return (
    <div className="aav2-voice" role="region" aria-label="Voice mode">
      <AssistantAvatar imageUrl={imageUrl} size="voice" />
      <div className="aav2-voice-text" aria-live="polite">
        <b>{title}</b>
        <small>{detail}</small>
      </div>
      <span className="aav2-voice-bars" data-active={String(isSoundActive)} aria-hidden="true">
        <i />
        <i />
        <i />
        <i />
        <i />
      </span>
      <button
        type="button"
        className="aav2-voice-mic"
        aria-pressed={isMuted}
        aria-label={isMuted ? "Turn the mic on" : "Turn the mic off"}
        title={isMuted ? "Turn the mic on" : "Turn the mic off"}
        onClick={toggleMute}
      >
        <LendyIcon name={isMuted ? "micOff" : "mic"} size={16} />
      </button>
      <button type="button" className="aav2-voice-end" onClick={end}>
        End
      </button>
    </div>
  );
}
